'use client';

import { flexRender, getCoreRowModel, useReactTable, ColumnDef, getPaginationRowModel } from '@tanstack/react-table';
import { ChevronIcon } from 'mode-shared/icons';
import { twMerge } from 'tailwind-merge';
import { useScreenSize } from 'mode-shared/hooks/useScreenSize';

// Add TypeScript declaration for column meta
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    sticky?: boolean;
  }
}

export const Table = <T,>({
  data,
  columns,
  pageSize,
  showPagination = true,
}: {
  data: T[];
  columns: ColumnDef<T>[];
  pageSize?: number;
  showPagination?: boolean;
}) => {
  const { isMobile } = useScreenSize();
  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    initialState: {
      pagination: {
        pageSize: pageSize || (isMobile ? 10 : 20),
      },
    },
  });

  return (
    <div className="w-full max-w-full">
      <div className="w-full max-w-full overflow-x-auto">
        <table className="w-full border-collapse bg-neutral-1000 text-white">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className="border-b border-neutral-800" key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={twMerge(
                      'text-center bg-grey-950 py-3 px-4 text-grey-400 font-medium border-r border-neutral-800 last:border-r-0 whitespace-nowrap',
                      header.column.columnDef.meta?.sticky && 'sticky left-0 z-30'
                    )}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-neutral-900 border-b border-neutral-800 text-grey-100">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={twMerge(
                      'py-3 px-4 text-center border-r border-neutral-800 last:border-r-0 whitespace-nowrap',
                      cell.column.columnDef.meta?.sticky && 'sticky left-0 z-20 bg-grey-950'
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex w-full justify-end px-4 py-3 border-t border-grey-900">
          <div className="flex gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="w-8 h-8 flex items-center justify-center text-sm rounded-lg border border-grey-900 disabled:opacity-50"
            >
              <ChevronIcon className="w-3 h-3 rotate-90" />
            </button>

            {(() => {
              const currentPage = table.getState().pagination.pageIndex + 1;
              const totalPages = table.getPageCount();
              const pagesToShow = [];

              // Always show current page
              pagesToShow.push(currentPage);

              // Add one page before if it exists
              if (currentPage > 1) {
                pagesToShow.unshift(currentPage - 1);
              }

              // Add one page after if it exists
              if (currentPage < totalPages) {
                pagesToShow.push(currentPage + 1);
              }

              // If we still have room and pages available, add one more
              if (pagesToShow.length < 3) {
                if (currentPage === 1 && totalPages >= 3) {
                  pagesToShow.push(currentPage + 2);
                } else if (currentPage === totalPages && totalPages >= 3) {
                  pagesToShow.unshift(currentPage - 2);
                }
              }

              return pagesToShow.map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => table.setPageIndex(pageNum - 1)}
                  disabled={table.getState().pagination.pageIndex === pageNum - 1}
                  className={`w-8 h-8 flex items-center justify-center text-sm rounded-lg border border-grey-900 opacity-50 disabled:opacity-100 disabled:bg-grey-900 disabled:text-neutral-100 hover:bg-neutral-900`}
                >
                  {pageNum}
                </button>
              ));
            })()}

            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="w-8 h-8 flex items-center justify-center text-sm rounded-lg border border-grey-900 disabled:opacity-50"
            >
              <ChevronIcon className="w-3 h-3 -rotate-90" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
